/**
 * Adree Theme — Animation Engine
 * IntersectionObserver-based scroll reveals + animated counters
 * Re-triggers on every viewport entry/exit
 */

/* global performance, requestAnimationFrame, cancelAnimationFrame, IntersectionObserver */

// ---- Utility: detect reduced motion preference ----
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// ---- Reveal Animations ----
function initRevealAnimations() {
  const revealElements = document.querySelectorAll('.js-reveal');

  if (!revealElements.length) {
    return;
  }

  // If reduced motion is preferred, reveal everything immediately and bail
  if (prefersReducedMotion()) {
    revealElements.forEach((el) => el.classList.add('is-revealed'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
        } else {
          entry.target.classList.remove('is-revealed');
        }
      });
    },
    {
      threshold: 0.12,
      rootMargin: '0px 0px -60px 0px',
    }
  );

  revealElements.forEach((el) => observer.observe(el));
}

// ---- Counter Animation ----

/**
 * Parse a numeric target from a string like "110+", "$2.5M", "99%"
 */
function parseCounterTarget(text) {
  const match = text.match(/(-?[\d.,]+)/);
  if (!match) {
    return { value: 0, prefix: '', suffix: text };
  }
  const raw = match[1];
  const value = parseFloat(raw.replace(/,/g, ''));
  const idx = text.indexOf(raw);
  const prefix = text.slice(0, idx);
  const suffix = text.slice(idx + raw.length);
  return { value, prefix, suffix };
}

/**
 * Ease-out cubic
 */
function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Animate a single counter element
 */
function animateCounter(el, target, prefix, suffix, duration) {
  const startTime = performance.now();

  function update(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = easeOutCubic(progress);

    // Determine if target is integer or float
    const isFloat = target % 1 !== 0;
    const current = isFloat
      ? parseFloat((eased * target).toFixed(1))
      : Math.round(eased * target);

    el.textContent = prefix + current + suffix;

    if (progress < 1) {
      el._rafId = requestAnimationFrame(update);
    } else {
      el.textContent = prefix + target + suffix;
      el._rafId = null;
    }
  }

  cancelCounter(el);
  el._rafId = requestAnimationFrame(update);
}

/**
 * Cancel any running counter animation
 */
function cancelCounter(el) {
  if (el._rafId) {
    cancelAnimationFrame(el._rafId);
    el._rafId = null;
  }
}

/**
 * Initialize all counters
 */
function initCounters() {
  const counters = document.querySelectorAll('.js-counter');

  if (!counters.length) {
    return;
  }

  // If reduced motion is preferred, show final values and bail
  if (prefersReducedMotion()) {
    counters.forEach((counter) => {
      const targetVal = counter.dataset.counterTarget;
      if (targetVal !== undefined) {
        const prefix = counter.dataset.counterPrefix || '';
        const suffix = counter.dataset.counterSuffix || '';
        counter.textContent = prefix + targetVal + suffix;
      }
    });
    return;
  }

  counters.forEach((counter) => {
    // Read data attributes first; fallback to parsing current text
    let target = parseFloat(counter.dataset.counterTarget);
    let prefix = counter.dataset.counterPrefix || '';
    let suffix = counter.dataset.counterSuffix || '';

    if (Number.isNaN(target)) {
      const parsed = parseCounterTarget(counter.textContent.trim());
      target = parsed.value;
      prefix = parsed.prefix;
      suffix = parsed.suffix;
      // Persist so re-triggers stay consistent
      counter.dataset.counterTarget = String(target);
      counter.dataset.counterPrefix = prefix;
      counter.dataset.counterSuffix = suffix;
    }

    const duration = parseInt(counter.dataset.counterDuration, 10) || 1500;

    // Reset to zero before observation
    counter.textContent = prefix + '0' + suffix;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounter(counter, target, prefix, suffix, duration);
          } else {
            cancelCounter(counter);
            counter.textContent = prefix + '0' + suffix;
          }
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(counter);
  });
}

// ---- Lazy image shimmer removal ----
function initShimmerCleanup() {
  const images = document.querySelectorAll('img[loading="lazy"]');

  images.forEach((img) => {
    const onLoad = () => {
      img.classList.add('is-loaded');
      const shimmerParent = img.closest('.shimmer');
      if (shimmerParent) {
        shimmerParent.classList.add('is-loaded');
      }
    };

    if (img.complete) {
      onLoad();
    } else {
      img.addEventListener('load', onLoad, { once: true });
    }
  });
}

// ---- Section Navigation ----

/**
 * Update CSS variable for the sticky header height so the section nav
 * sticks exactly below the floating header.
 */
function updateStickyOffset() {
  const floating = document.querySelector('.site-header__floating');
  const inner = document.querySelector('.site-header__floating-inner');
  if (!floating || !inner) {
    return;
  }

  const topOffset = parseFloat(getComputedStyle(floating).top) || 0;
  const height = inner.getBoundingClientRect().height;
  const total = Math.ceil(topOffset + height);
  document.documentElement.style.setProperty('--header-sticky-offset', `${total}px`);
}

/**
 * Initialize sticky section nav: smooth scroll, active states, visibility.
 */
function initSectionNav() {
  const nav = document.querySelector('.section-nav');
  const track = document.querySelector('.section-nav__track');
  const links = document.querySelectorAll('.section-nav__link');
  if (!nav || !track || !links.length) {
    return;
  }

  // Create moving indicator
  const indicator = document.createElement('div');
  indicator.className = 'section-nav__indicator';
  track.appendChild(indicator);

  // Set initial position to first link
  const firstLink = links[0];
  if (firstLink) {
    updateIndicator(firstLink);
  }

  // Function to update indicator position
  function updateIndicator(activeLink) {
    if (!activeLink || !indicator) {
      return;
    }

    const trackRect = track.getBoundingClientRect();
    const linkRect = activeLink.getBoundingClientRect();

    const relativeLeft = linkRect.left - trackRect.left;
    const width = linkRect.width;

    indicator.style.transform = `translateX(${relativeLeft}px)`;
    indicator.style.width = `${width}px`;
  }

  // 1. Measure header and set sticky offset
  updateStickyOffset();
  window.addEventListener('resize', () => {
    updateStickyOffset();
    // Update indicator position on resize
    const activeLink = document.querySelector('.section-nav__link.is-active');
    if (activeLink) {
      updateIndicator(activeLink);
    }
  }, { passive: true });

  // 2. Smooth scroll on click
  links.forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const targetId = link.getAttribute('href');
      const target = document.querySelector(targetId);
      if (!target) {
        return;
      }

      const headerOffset = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-sticky-offset')
      ) || 168;
      const navHeight = nav.offsetHeight || 60; // fallback if nav is hidden
      const extra = 16; // small breathing room
      const y = target.getBoundingClientRect().top + window.scrollY - headerOffset - navHeight - extra;

      window.scrollTo({
        top: Math.max(0, y),
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });

      // Update active immediately for feedback
      links.forEach((l) => l.classList.remove('is-active'));
      link.classList.add('is-active');
      link.setAttribute('aria-current', 'true');
      updateIndicator(link);
    });
  });

  // 3. Show/hide nav after crossing halfway through Hero slider.
  const heroSection = document.getElementById('overview');
  if (heroSection) {
    let ticking = false;

    function checkNavVisibility() {
      const scrollY = window.scrollY;
      const heroTop = heroSection.getBoundingClientRect().top + scrollY;
      const heroHeight = heroSection.offsetHeight;
      // Trigger after user has crossed halfway through the Hero slider.
      const triggerPoint = heroTop + (heroHeight / 2);

      if (scrollY >= triggerPoint) {
        nav.classList.add('is-visible');
      } else {
        nav.classList.remove('is-visible');
      }
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(checkNavVisibility);
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    checkNavVisibility(); // initial check on load
  }

  // 4. Track active section while scrolling
  const sections = [];
  links.forEach((link) => {
    const id = link.dataset.section;
    const section = document.getElementById(id);
    if (section) {
      sections.push({ id, link, section });
    }
  });

  if (!sections.length) {
    return;
  }

  // Scroll-based active tracking (more reliable than IntersectionObserver for short sections)
  const headerOffset = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--header-sticky-offset')
  ) || 168;

  function updateActiveSection() {
    const scrollY = window.scrollY;
    const viewportHeight = window.innerHeight;
    const triggerOffset = headerOffset + 100;

    let activeId = null;
    let minDistance = Infinity;

    sections.forEach(({ id, section }) => {
      const rect = section.getBoundingClientRect();
      const sectionTop = rect.top + scrollY;
      const sectionBottom = sectionTop + rect.height;

      // Check if section is in viewport or just above/below
      const isVisible = sectionBottom > scrollY + triggerOffset && sectionTop < scrollY + viewportHeight;

      if (isVisible) {
        // Calculate distance from viewport top to section top
        const distance = Math.abs(rect.top - triggerOffset);
        if (distance < minDistance) {
          minDistance = distance;
          activeId = id;
        }
      }
    });

    // Fallback: if no section is visible, use the last one that's above viewport
    if (!activeId) {
      for (let i = sections.length - 1; i >= 0; i--) {
        const { id, section } = sections[i];
        const rect = section.getBoundingClientRect();
        const sectionTop = rect.top + scrollY;
        if (sectionTop < scrollY + triggerOffset) {
          activeId = id;
          break;
        }
      }
    }

    if (activeId) {
      let activeLink = null;
      sections.forEach(({ id, link }) => {
        if (id === activeId) {
          link.classList.add('is-active');
          link.setAttribute('aria-current', 'true');
          activeLink = link;
        } else {
          link.classList.remove('is-active');
          link.removeAttribute('aria-current');
        }
      });
      updateIndicator(activeLink);
    }
  }

  let scrollTicking = false;
  function onScroll() {
    if (!scrollTicking) {
      window.requestAnimationFrame(() => {
        updateActiveSection();
        scrollTicking = false;
      });
      scrollTicking = true;
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  updateActiveSection(); // Initial check
}

// ---- Trust Slider ----
function initTrustSlider() {
  const slider = document.querySelector('.trust-slider');
  if (!slider) return;

  const track = slider.querySelector('.trust-slider__track');
  const originalSlides = slider.querySelectorAll('.trust-slider__slide');

  if (!originalSlides.length) return;

  // Detect RTL
  const isRTL = document.documentElement.dir === 'rtl' || document.body.classList.contains('rtl');

  // Duplicate slides for infinite loop
  const slides = [...originalSlides];
  for (let i = 0; i < 4; i++) {
    slides.forEach(slide => {
      const clone = slide.cloneNode(true);
      track.appendChild(clone);
    });
  }

  let position = 0;
  let speed = 1;
  let animationId = null;

  function animate() {
    const slideWidth = originalSlides[0].offsetWidth + parseInt(getComputedStyle(track).gap);

    // In RTL, move in opposite direction
    if (isRTL) {
      position += speed;
      // Reset position when we've moved past the first set of slides
      const totalWidth = slideWidth * originalSlides.length;
      if (position >= totalWidth) {
        position -= totalWidth;
      }
    } else {
      position -= speed;
      // Reset position when we've moved past the first set of slides
      const totalWidth = slideWidth * originalSlides.length;
      if (Math.abs(position) >= totalWidth) {
        position += totalWidth;
      }
    }

    track.style.transform = `translateX(${position}px)`;
    animationId = requestAnimationFrame(animate);
  }

  // Start animation
  animationId = requestAnimationFrame(animate);

  // Pause on hover
  slider.addEventListener('mouseenter', () => {
    cancelAnimationFrame(animationId);
  });

  slider.addEventListener('mouseleave', () => {
    animationId = requestAnimationFrame(animate);
  });

  // Update on resize
  window.addEventListener('resize', () => {
    position = 0;
  });
}

// ---- Partners Slider ----
function initPartnersSlider() {
  const slider = document.querySelector('.partners-slider');
  if (!slider) return;

  const track = slider.querySelector('.partners-slider__track');
  const originalSlides = slider.querySelectorAll('.partners-slider__slide');

  if (!originalSlides.length) return;

  // Detect RTL
  const isRTL = document.documentElement.dir === 'rtl' || document.body.classList.contains('rtl');

  // Duplicate slides for infinite loop
  const slides = [...originalSlides];
  for (let i = 0; i < 4; i++) {
    slides.forEach(slide => {
      const clone = slide.cloneNode(true);
      track.appendChild(clone);
    });
  }

  let position = 0;
  let speed = 1;
  let animationId = null;

  function animate() {
    const slideWidth = originalSlides[0].offsetWidth + parseInt(getComputedStyle(track).gap);

    // In RTL, move in opposite direction
    if (isRTL) {
      position += speed;
      // Reset position when we've moved past the first set of slides
      const totalWidth = slideWidth * originalSlides.length;
      if (position >= totalWidth) {
        position -= totalWidth;
      }
    } else {
      position -= speed;
      // Reset position when we've moved past the first set of slides
      const totalWidth = slideWidth * originalSlides.length;
      if (Math.abs(position) >= totalWidth) {
        position += totalWidth;
      }
    }

    track.style.transform = `translateX(${position}px)`;
    animationId = requestAnimationFrame(animate);
  }

  // Start animation
  animationId = requestAnimationFrame(animate);

  // Pause on hover
  slider.addEventListener('mouseenter', () => {
    cancelAnimationFrame(animationId);
  });

  slider.addEventListener('mouseleave', () => {
    animationId = requestAnimationFrame(animate);
  });

  // Update on resize
  window.addEventListener('resize', () => {
    position = 0;
  });
}

// ---- Public API ----
function initAnimations() {
  initRevealAnimations();
  initCounters();
  initShimmerCleanup();
  initSectionNav();
  initTrustSlider();
  initPartnersSlider();
}

export { initAnimations };
